import { Observable } from "rxjs";
import { WebShopMode } from "./IWebShopService";

export interface IRemoteGroupOrderService {
  /** is order delivery */
  readonly isGroupDelivery: boolean;
  /**organizational data */
  readonly groupManageData: IGroupManageData;
  /** location data */
  readonly locationData$: Observable<ILocationData>
  /** Group name */
  readonly groupName: string;
  /** True when a group name is available either from the current session or a cached/active group */
  readonly hasDefinedGroupName: boolean;
  /** True when a group order time (deadline) has been selected for the remote group order */
  readonly hasDefinedGroupOrderTime: boolean;
  /** Group creator name */
  readonly creatorName: string;
  /** Group creator id */
  readonly creatorId: number;
  /**Number of group members */
  readonly memberCount$: Observable<number>
  /** Group code */
  readonly groupId: number;
  /** User is ready */
  readonly stateReady$: Observable<boolean>;
  /** Items count  */
  readonly groupBasketItemsCount$: Observable<number>;
  /** Total price */
  readonly groupBasketTotalPrice$: Observable<number>;
  /** All user have state 'Ready'*/
  readonly allMembersAreReady$: Observable<boolean>;
  /** Display group icon ( main nav) and  button (home page) */
  readonly displayGroupNav$: Observable<boolean>
  /** from this point member cann't add item */
  readonly groupStatusPaymentStarted$: Observable<boolean>;
  // Globalere Events
  /** members data changed */
  readonly membersChanged$: Observable<void>;
  /** items data changed */
  readonly itemsChanged$: Observable<void>;
  /** group closed */
  readonly groupDeleted$: Observable<void>;
  /** group status changed */
  readonly groupStatusChanged$: Observable<void>;

  /** Retrieves the data of the currently selected location where the order is to be placed */
  getCurrentLocationData(): Promise<void>;
  /** Sets the name of the creator */
  setCreatorName(creatorName: string): string;
  /** Sets the name of the group */
  setGroupName(groupName: string): string;
  /** Sets  the selected service time  */
  setSelectedServiceTime(serviceTimeInfo: string): string;
  /** Sets the selected order end time  */
  setSelectedOrderTime(orderTimeInfo: string): string;
  /** Create group */
  createGroup(): Promise<CreateGroupResult>
  /**Leave group */
  leaveGroup(): Promise<void>;
  /** Join group */
  joinGroup(groupId: number, username: string): Promise<any>;
  /** Set user state 'Ready' */
  changeAndSetUserStateReady(userId: string | number, ready: boolean): void
}

export interface IGroupManageData {
  /** Group id */
  readonly groupId: number;
  /** user Id */
  readonly userId: number;
  /** Group name */
  readonly groupName: string;
  /** Group creator name */
  readonly creatorName: string;
  /** Delivery address*/
  readonly groupDeliveryAddress: string;
  /** Selected time delivery/pickup */
  readonly selectedServiceTime: string;
  /**Selected order end time*/
  readonly selectedOrderTime: string;
}

export interface ILocationData {
  readonly name: string;
  readonly street: string;
  readonly zipCode: string;
  readonly city: string;
}

export type HttpStatusCode = number;

export enum EntryStatus {
  /** User an Platz zu setzen war erfolgrich */
  Okay = 0,
  /** User an Platz zu setzen war nicht möglich */
  Fail = 1,
  /** Username ist falsch, vergeben oder gesterrt */
  WrongUserName = 2,
  /** Gruppenname ist falsch, vergeben oder gesterrt */
  WrongGroupName = 3,
  /** User konnte Gruppe nicht beitreten, Gruppe hat das Limit erreicht */
  GroupFull = 4,
  /** Gruppe konnte nicht erzeugt werden, Tisch hat das Limit erreicht */
  TableFull = 5,
  /** Fehler beim verarbeiten */
  Error = -1,
}

export interface Member {
  MemberId: number,
  Name: string,
  // see comment on property CreatorAccountId in Group
  // AccountId?: number,
  Status: MemberStatus,
}

export interface Extra {
  Id: number,
  Count: number,
}

export interface Article {
  Id: number,
  Count: number,
  // it seems like MakePurchaseOrder does not allow nullable comments, so we don't allow it either
  Comment: string,
  Extras: Extra[],
}

export enum PreOrderVariant {
  PickupGroup = WebShopMode.PickupGroup,
  DeliveryGroup = WebShopMode.DeliveryGroup,
}

export interface Group {
  Id: number,
  Name: string,
  PreOrderType: { Variant: PreOrderVariant, Address?: string },
  HypCusNr: number,
  OrderTimestamp: string,
  ServiceTimestamp: string,
  // server Group has this data, but client should not know about this data
  // it is also nullable in server, so commenting it out for now
  // CreatorAccountId?: number,
  // TODO: might be changed to one comment
  Comments: string[],
  MemberLimit: number,
  Members: Member[],
  // json only allows string keys, so this is a number typed as a string
  Basket: { [MemberId: string]: Article[] },
  Status: Status,
  /**
   * Total zu zahlender Betrag (baseprice - discountTotal + deposit + tip + deliveryCost).\
   * Entspricht AddPurchaseOrderDataModel.grossprice
  */
  TotalPaidPrice: number,
  OrderCostLimit: number,
}

export interface GroupUiData {
  members: GroupMemberUiData[];
  basket_items: GroupBasketItemUiData[];
}

export interface GroupMemberUiData {
  isGroupCreator: boolean;
  groupId: number;
  userId: number;
  name: string;
  stateReady: MemberStatus;
  isOrdering: boolean;
}

export interface GroupBasketItemUiData {
  article: Article,
  userId: number,
  id: number,
  productId: number,
  // TODO: name_de should probably be name
  name_de: string,
  name_en: string,
  valid_date?: number,
  deposit: number | null,
  extras: IRemoteGroupBasketItemExtra[], //! TODO muss durch eine classe ersetzt werden
  count: number,
  price: number,
  priceWithoutExtras: number,
  comment: string,
}

export interface IRemoteGroupBasketItemExtra {
  /** Extra with subindex */
  readonly subIndex: number;
  /** Count of the product extra */
  readonly count: number;
  /** Extra name */
  title: string; // todo muss read only werden
  title_de: string; // todo muss read only werden
  title_en: string; // todo muss read only werden
  /** product.id vom */
  readonly id: number;
}

export interface PostGroupArgs {
  Username: string,
  GroupName: string,
  Address?: string,
  ServiceTimeStamp: string,
  OrderTimeStamp: string,
  HypCusNr: number,
}

export enum CreateGroupVariant {
  /** The group was successfully defined. */
  Okay,
  /** The group createion failed. */
  Fail,
  /** The maximum number of groups has been reached. */
  maxGroupCountReached,
  /** Latest order completion time is too soon (less than the required minutes from the current time(Portal)).*/
  isTooSoon,
  /**Any other conflict case */
  TimeConflict,
}


export type CreateGroupResult = {
  Variant: CreateGroupVariant.Okay,
  Group: Group,
}
  | {
    Variant: CreateGroupVariant.Fail,
  }
  | {
    Variant: CreateGroupVariant.maxGroupCountReached,
  }
  | {
    Variant: CreateGroupVariant.isTooSoon,
  }
  | {
    Variant: CreateGroupVariant.TimeConflict,
  }

export enum Status {
  NotReady,
  Ready,
  Finishing,
  Finished,
}

export enum MemberStatus {
  NotReady,
  Ready,
}